import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import imageCompression from 'browser-image-compression';
import mime from 'mime-types';

import { handleResponseCode } from '../actions/index';
import { FETCH_UPLOAD_SIGNED_URL, FETCH_LARGE_UPLOAD_SIGNED_URL } from '../network/api';
import Dropzone from './Dropzone';
import FileManager from './FileManager';
import { HHAlert, hlog } from '../common/util';
import { ALERT_TYPE } from '../common/constant';
// /*
//   // USAGE
//   <FileUploader
//     accept={'images/*'}
//     handleUploadBtnClick={this.handleUploadBtnClick}
//     cropRatios={['16:9', 'free', '1:1']}
//   />
//   */
//
const initState = {
  isUploading: false,
  acceptedFile: null,
  rejectedFile: null,
  isImage: false,
  activeIndex: 0,
  percentCompleted: null,
  maxSize: 4000000
};

// const MAX_FILE_SIZE = 30000000; // 30MB

const largeFileTypes = ['user_project_content', 'event_submission_content', 'event_resource_content'];


class Uploader extends Component {
  state = { ...initState };


  componentDidMount() {
    this.setMaxFileSize(this.props.fileType);
  }

  setMaxFileSize = (fileType) => {
    if (largeFileTypes.includes(fileType)) {
      this.setState({
        maxSize: 50000000
      });
    }
  }

  handleFileDrop = (acceptedFiles, rejectedFiles) => {
    const { maxSize } = this.state;
    const acceptedFile = acceptedFiles[0] ? acceptedFiles[0] : null;
    const rejectedFile = rejectedFiles[0] ? rejectedFiles[0] : null;
    const isImage = acceptedFile && acceptedFile.type.includes('image');

    const error =
      rejectedFile && rejectedFile.size > maxSize
        ? `Max file size ${maxSize / 1000000}MB`
        : 'Invalid file type';

    this.setState({
      acceptedFile,
      rejectedFile,
      error,
      isImage,
      activeIndex: 1
    });
  };

  handleReset = () => {
    this.setState({ ...initState });
  };

  fetchUploadUrl = async (parameter) => {
    try {
      const res = await axios.post(FETCH_UPLOAD_SIGNED_URL, parameter);
      const { code, response } = res.data;
      if (response && code === 200) {
        const data = await res.data.response;
        return data;
      }
      handleResponseCode(code);
      return {};
    } catch (err) {
      HHAlert({ id: 'alert.error.uploadFailed' }, ALERT_TYPE.ERROR);
      hlog(err);
      return {};
    }
  };

  fetchUploadUrlCH = async (parameter) => {
    try {
      const config = {
        method: 'post',
        url: FETCH_UPLOAD_SIGNED_URL,
        headers: {
          'Content-Type': 'application/json'
        },
        data: parameter
      };
      const res = await axios(config);
      const { code, response } = res.data;
      if (response && code === 200) {
        const data = await res.data.response;
        return data;
      }
      handleResponseCode(code);
      return {};
    } catch (err) {
      HHAlert({ id: 'alert.error.uploadFailed' }, ALERT_TYPE.ERROR);
      hlog(err);
      return {};
    }
  }

  fetchLargeFileUploadSignedUrl = async (parameter) => {
    try {
      const res = await axios.post(FETCH_LARGE_UPLOAD_SIGNED_URL, parameter);
      const { code, response } = res.data;
      if (response && code === 200) {
        const data = await res.data.response;
        return data;
      }
      handleResponseCode(code);
      return {};
    } catch (err) {
      HHAlert({ id: 'alert.error.uploadFailed' }, ALERT_TYPE.ERROR);
      hlog(err);
      return {};
    }
  }

  fetchLargeFileUploadSignedUrlCH = async (parameter) => {
    try {
      const config = {
        method: 'post',
        url: FETCH_LARGE_UPLOAD_SIGNED_URL,
        headers: {
          'Content-Type': 'application/json'
        },
        data: parameter
      };
      const res = await axios(config);
      const { code, response } = res.data;
      if (response && code === 200) {
        const data = await res.data.response;
        return data;
      }
      handleResponseCode(code);
      return {};
    } catch (err) {
      HHAlert({ id: 'alert.error.uploadFailed' }, ALERT_TYPE.ERROR);
      hlog(err);
      return {};
    }
  }

  uploadToBucket = async (config) => {
    try {
      const res = await axios(config);
      const { status } = res;
      if (status === 204 || status === 200) {
        HHAlert({ id: 'alert.success.uploadSuccess' }, ALERT_TYPE.SUCCESS);
        return res;
      }
      handleResponseCode(status);
      return {};
    } catch (err) {
      HHAlert({ id: 'alert.error.uploadFailed' }, ALERT_TYPE.ERROR);
      hlog(err);
      return {};
    }
  };

  compressImage = async (file, options) => {
    const compressed = await imageCompression(file, options);
    return compressed;
  };

  handleFileUpload = async file => {
    this.setState({ isUploading: true });

    const {
      onChange,
      postChange,
      user,
      maxImageSizeMB,
      fileType,
      slug,
      teamId,
      contentId
    } = this.props;

    const userId = get(user, 'profile.basicInfo.id');

    if (userId) {
      const config = {
        onUploadProgress: progressEvent => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          this.setState({ percentCompleted: percentCompleted - 1 });
        }
      };
      const maxSizeMB = maxImageSizeMB || 1;
      const options = {
        maxSizeMB,
        useWebWorker: true
      };
      const userToken = get(user, 'token');
      const eventSlug = slug || '';
      const userTeamId = teamId || '';

      const fileToUpload = file;
      const { size } = fileToUpload;

      const extension = fileToUpload.name.split('.').pop();
      const mimeType = mime.contentType(extension);

      let parameter;
      // parameter for large files
      if (largeFileTypes.includes(fileType)) {
        let objectKeyTemplate;
        if (fileType === 'user_project_content') {
          const username = get(user, 'profile.basicInfo.username');
          objectKeyTemplate = {
            username,
            project_id: contentId || ''
          };
        } else if (fileType === 'event_submission_content') {
          objectKeyTemplate = {
            event_slug: eventSlug,
            submission_id: contentId || ''
          };
        } else {
          objectKeyTemplate = {
            event_slug: eventSlug,
            resource_id: contentId || ''
          };
        }
        parameter = {
          token: userToken,
          MIMEType: mimeType,
          fileType,
          byteSize: size,
          fileExtension: `${extension}`,
          eventSlug,
          objectKeyTemplate
        };
      } else {
        parameter = {
          token: userToken,
          MIMEType: mimeType,
          fileType,
          byteSize: size,
          fileExtension: `${extension}`,
          eventSlug,
          teamId: userTeamId
        };
      }

      const domain = window.location.href;
      if (domain.includes('event.hackhub.cn')) {
        let res;
        if (largeFileTypes.includes(fileType)) {
          res = await this.fetchLargeFileUploadSignedUrlCH(parameter);
        } else {
          res = await this.fetchUploadUrlCH(parameter);
        }
        if (isEmpty(res)) {
          window.heap.clearEventProperties();
          window.heap.addEventProperties({ HHEFileUploadResult: 0 });
          return;
        }
        const { url, objectKey } = res;
        let uploadFile = file;

        if (this.state.isImage && file.size / 1000000 > maxSizeMB) {
          uploadFile = this.compressImage(file, options);
        }

        const data = uploadFile;

        const uploadConfig = {
          method: 'put',
          url,
          headers: {
            'Content-Type': mimeType
          },
          data,
          onUploadProgress: config.onUploadProgress
        };
        const uploadRes = await this.uploadToBucket(uploadConfig);
        if (isEmpty(uploadRes)) return;
        onChange(objectKey);
        postChange();
      } else {
        let res;
        // upload large file
        if (largeFileTypes.includes(fileType)) {
          res = await this.fetchLargeFileUploadSignedUrl(parameter);
        } else {
          res = await this.fetchUploadUrl(parameter);
        }
        if (isEmpty(res)) {
          window.heap.clearEventProperties();
          window.heap.addEventProperties({ HHEFileUploadResult: 0 });
          return;
        }

        const { url, fields } = res;
        const { key, bucket, Policy } = fields;
        const algorithm = fields['X-Amz-Algorithm'];
        const credential = fields['X-Amz-Credential'];
        const date = fields['X-Amz-Date'];
        const signature = fields['X-Amz-Signature'];
        const data = new FormData();
        let uploadFile = file;

        data.append('key', key);
        data.append('bucket', bucket);
        data.append('X-Amz-Algorithm', algorithm);
        data.append('X-Amz-Credential', credential);
        data.append('X-Amz-Date', date);
        data.append('Policy', Policy);
        data.append('X-Amz-Signature', signature);
        data.append('Content-Type', mimeType);

        if (this.state.isImage && file.size / 1000000 > maxSizeMB) {
          uploadFile = this.compressImage(file, options);
        }
        data.append('file', uploadFile);

        const uploadConfig = {
          method: 'POST',
          url,
          data,
          onUploadProgress: config.onUploadProgress
        };

        const uploadRes = await this.uploadToBucket(uploadConfig);
        if (isEmpty(uploadRes)) return;
        onChange(key);
        postChange();
      }
    }
  };

  render() {
    const { accept, cropRatios } = this.props;
    const {
      acceptedFile,
      rejectedFile,
      error,
      isImage,
      activeIndex,
      percentCompleted,
      isUploading,
      maxSize
    } = this.state;

    return (
      <Fragment>
        {activeIndex === 0 && (
          <Dropzone
            accept={accept}
            onDrop={this.handleFileDrop}
            maxSize={maxSize}
          />
        )}

        {activeIndex === 1 && (
          <FileManager
            acceptedFile={acceptedFile}
            rejectedFile={rejectedFile}
            error={error}
            isImage={isImage}
            handleUploadBtnClick={this.handleFileUpload}
            handleReset={this.handleReset}
            cropRatios={cropRatios}
            percentCompleted={percentCompleted}
            isUploading={isUploading}
          />
        )}
      </Fragment>
    );
  }
}

Uploader.propTypes = {
  accept: PropTypes.string,
  onChange: PropTypes.func,
  postChange: PropTypes.func,
  cropRatios: PropTypes.arrayOf(PropTypes.string)
};

Uploader.defaultProps = {
  accept: undefined,
  onChange: () => {},
  postChange: () => {},
  cropRatios: []
};

export default connect(({ user }) => ({ user }))(Uploader);



// WEBPACK FOOTER //
// ./src/components/uploader/index.js